import { experimentsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

/**
 * Pipeline lifecycle states mirrored from workflow-service's
 * `ExperimentPipelineStatus`. Drives retry-button enablement and the
 * timeline narration when the pipeline has terminated.
 */
export const PipelineStatus = {
  IN_PROGRESS: 'IN_PROGRESS',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
  COMPLETED: 'COMPLETED',
  REJECTED: 'REJECTED',
  FAILED: 'FAILED',
} as const;

export type PipelineStatus = (typeof PipelineStatus)[keyof typeof PipelineStatus];

/**
 * Pipeline entry point — backend enum. CURATION re-runs the full pipeline;
 * DELIVERY re-attempts the GMC push only (no curation, enrichment, or
 * approval). ENRICHMENT is reserved on the backend and not exposed here.
 */
export const PipelineStartStage = {
  CURATION: 'CURATION',
  DELIVERY: 'DELIVERY',
} as const;

export type PipelineStartStage = (typeof PipelineStartStage)[keyof typeof PipelineStartStage];

/**
 * One pipeline stage on the live progress timeline (Curation → Enrichment →
 * Approval → Delivery). Backend stamps these from `ExperimentWorkflowImpl`
 * at every stage boundary; the UX renders the list as a vertical timeline
 * inside the "Delivery in Progress" card so operators see exactly which
 * step is in flight without staring at the spinner alone.
 */
export type PipelineStageStatus = 'in_progress' | 'completed' | 'awaiting' | 'failed';

export interface PipelineStage {
  name: string;
  /** Wire-permissive: backend sends one of {@link PipelineStageStatus}; unknown values fall through to "pending" in the UX. */
  status: string;
  count?: number;
  startedAt?: string;
  completedAt?: string;
  /** When true the stage was skipped — e.g. Delivery suppressed means no products are pushed to the platform. */
  suppressed?: boolean;
}

/** Converts a backend stage name (e.g. `"PostScoring"`) into the i18n key
 *  suffix used under `experiments:detail.launch.stages.*` — splits on
 *  camelCase boundaries and lowercases the result (`"post_scoring"`). */
export function pipelineStageI18nKey(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

/** Humanises a backend stage name (e.g. `"PostScoring"` → `"Post Scoring"`)
 *  for use as the i18n `defaultValue` so any *new* stage the backend adds
 *  renders something readable instead of breaking the UI. */
export function humanisePipelineStageName(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}

/**
 * Wire response from `GET /experiments/{id}/pipeline/status`. Mirrors
 * workflow-service's `ExperimentPipelineStatusResponseDto` — field names
 * are the unprefixed forms (`status`, `workflowId`, …), not the entity
 * column names. `@JsonInclude(NON_NULL)` on the server means fields are
 * absent (not null) when unset, so consumers must guard with `?? null`.
 */
export interface PipelineStatusResponse {
  experimentId?: string;
  workflowId?: string;
  status?: PipelineStatus;
  startedAt?: string;
  completedAt?: string;
  stages?: PipelineStage[];
}

/** Wire response from `POST /experiments/{id}/pipeline/trigger`. */
export interface TriggerPipelineResponse {
  workflowId: string;
  runId: string;
  status: string;
}

/**
 * Recommendation from `GET /experiments/{id}/pipeline/preview`. Mirrors
 * workflow-service's `PipelinePreviewResponseDto.PreviewRecommendation`.
 *
 * - `PROCEED` — enough candidates, no prompt needed
 * - `WARN` — low candidate count, A/B split + outlier filter may eat all of
 *   them; the FE shows a banner but the trigger button stays enabled
 * - `BLOCK` — zero candidates, FE disables the trigger
 */
export type PipelinePreviewRecommendation = 'PROCEED' | 'WARN' | 'BLOCK';

/**
 * Wire response from `GET /experiments/{id}/pipeline/preview`. Read once
 * before showing the trigger button — no polling, since the candidate count
 * only changes after a fresh content-scoring run.
 */
export interface PipelinePreviewResponse {
  candidateCount: number;
  recommendation: PipelinePreviewRecommendation;
  /** Backend-formatted explanation, safe to render verbatim in a banner. */
  recommendationReason: string;
}

/**
 * Optional request body for the trigger endpoint. All fields optional.
 * `subBatchSize` overrides the env-bound default for the POST re-scoring
 * fan-out batch cap; absent falls back to the server default.
 */
export interface TriggerPipelinePayload {
  startFromStage?: PipelineStartStage;
  subBatchSize?: number;
}

/** Fetches the live pipeline-status row so the UI can derive retry eligibility + timeline state. */
export async function fetchPipelineStatus(experimentId: string): Promise<PipelineStatusResponse> {
  return experimentsClient
    .get(endpoints.experiments.pipelineStatus(experimentId))
    .json<PipelineStatusResponse>();
}

/**
 * Fetches the pre-trigger preview — drives the trigger button gate. Read once
 * on mount; no polling, since the candidate count only changes after a fresh
 * content-scoring run (a refetch on that mutation's success is enough).
 */
export async function fetchPipelinePreview(experimentId: string): Promise<PipelinePreviewResponse> {
  return experimentsClient
    .get(endpoints.experiments.pipelinePreview(experimentId))
    .json<PipelinePreviewResponse>();
}

/**
 * Triggers (or re-triggers) the experiment pipeline. `startFromStage`
 * controls the entry point — omitted = full pipeline; `DELIVERY` = retry
 * just the GMC push on a previously-approved run. Backend rejects on
 * pre-condition violations (no scoring, already in progress, etc.).
 */
export async function triggerPipeline(
  experimentId: string,
  payload: TriggerPipelinePayload = {},
): Promise<TriggerPipelineResponse> {
  return experimentsClient
    .post(endpoints.experiments.pipelineTrigger(experimentId), { json: payload })
    .json<TriggerPipelineResponse>();
}
